// Grid popover konumlandırması (tur bulgusu P3-17; 2026-10-07 kırpma düzeltmesi): hücre editörü /
// formül / raporlanan popover'ları tablo kabının İÇİNDE `position:absolute` duruyordu ve kabın
// `overflow`u popover'ı kırpıyordu (viewport'ta yer olsa bile en alt satırın popover'ı görünmüyordu).
// Artık üç popover da `createPortal(document.body)` + `position:fixed`; karar hep ÇAPA hücrenin
// `getBoundingClientRect`'inden (viewport koordinatı) verilir:
//   1) altta yer varsa hücrenin altı (varsayılan);
//   2) yoksa hücrenin ÜSTÜ (flip);
//   3) ikisi de sığmıyorsa viewport'a kıstırılır (`pinned`, kenarlara 8 px, gerekirse iç kaydırma).
// Tablo kabı kaydırılınca / pencere yeniden boyutlanınca / popover'ın kendi boyutu değişince
// (sekme, async yükleme) yeniden ölçülür (capture scroll + resize + ResizeObserver, rAF ile tek
// kare). Çapa hücre görünür alandan çıkarsa popover KAPANMAZ, viewport'a kıstırılır.
// Klavye/odak sözleşmesi çağıranlarda (Esc → hücreye dön, Enter → kaydet) değişmez: portal React
// ağacında KpiGrid'in çocuğu kalır, olaylar aynı yoldan köpürür.
// z-index katmanı: popover 90 < Modal/ConfirmDialog 100 < ClientPicker listesi 110.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

/** Çapa hücrenin viewport dikdörtgeni (getBoundingClientRect alt kümesi). */
export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

export interface PlacedPosition {
  /** viewport koordinatı (position:fixed) */
  top: number;
  left: number;
  placement: "below" | "above" | "pinned";
  /** popover viewport'tan uzunsa iç kaydırma tavanı (viewport − 16) */
  maxHeight?: number;
}

export const POPOVER_Z_INDEX = 90;
const EDGE = 8;
const GAP = 4;
/** popover hücrenin solundan bu kadar sola kayar (hücre sağa dayalı sayı, popover geniş) */
const LEFT_SHIFT = 120;

/** Saf karar (test edilebilir): çapa + ölçülmüş boyut + viewport → nihai konum. */
export function placePopover(
  anchor: AnchorRect,
  size: { height: number; width: number },
  viewport: { height: number; width: number },
): PlacedPosition {
  const vh = viewport.height;
  const vw = viewport.width;
  const cap = Math.max(120, vh - 2 * EDGE);
  const clipped = size.height > cap;
  const h = clipped ? cap : size.height;
  const left = Math.max(EDGE, Math.min(anchor.left - LEFT_SHIFT, vw - size.width - EDGE));
  const extra = clipped ? { maxHeight: cap } : {};

  const belowTop = anchor.bottom + GAP;
  if (belowTop >= EDGE && belowTop + h <= vh - EDGE) {
    return { top: belowTop, left, placement: "below", ...extra };
  }
  const aboveTop = anchor.top - GAP - h;
  if (aboveTop >= EDGE && aboveTop + h <= vh - EDGE) {
    return { top: aboveTop, left, placement: "above", ...extra };
  }
  // ne altta ne üstte yer var (ya da çapa görünür alan dışında) → viewport'a kıstır
  const top = Math.max(EDGE, Math.min(aboveTop, vh - h - EDGE));
  return { top, left, placement: "pinned", ...extra };
}

function measureOnce(
  el: HTMLElement,
  anchorEl: HTMLElement | null,
  fallback: AnchorRect,
): PlacedPosition {
  const r = el.getBoundingClientRect();
  // pinned modda ölçülen yükseklik maxHeight ile kırpılmış olabilir — doğal yüksekliği al
  const height = Math.max(r.height, el.scrollHeight);
  const a = anchorEl ? anchorEl.getBoundingClientRect() : fallback;
  return placePopover(
    { top: a.top, bottom: a.bottom, left: a.left },
    { height, width: r.width },
    { height: window.innerHeight, width: window.innerWidth },
  );
}

function samePos(a: PlacedPosition, b: PlacedPosition): boolean {
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.placement === b.placement &&
    a.maxHeight === b.maxHeight
  );
}

/**
 * `ref`: popover kökü; `getAnchor`: çapa hücre (her ölçümde yeniden okunur — satır yeniden
 * render olsa da güncel element). `deps`: ResizeObserver olmayan ortamlar için ek tetikleyici.
 */
export function usePopoverPlacement(
  ref: RefObject<HTMLElement>,
  getAnchor: () => HTMLElement | null,
  deps: readonly unknown[] = [],
): PlacedPosition {
  const getAnchorRef = useRef(getAnchor);
  getAnchorRef.current = getAnchor;
  // çapa kaybolursa (bant kapandı, satır filtrelendi) son bilinen dikdörtgen kullanılır
  const lastAnchor = useRef<AnchorRect>({ top: EDGE, bottom: EDGE, left: EDGE });
  const [pos, setPos] = useState<PlacedPosition>(() => {
    const a = getAnchor()?.getBoundingClientRect();
    if (a) lastAnchor.current = { top: a.top, bottom: a.bottom, left: a.left };
    return { top: lastAnchor.current.bottom + GAP, left: EDGE, placement: "below" };
  });

  const measure = useCallback((): void => {
    const el = ref.current;
    if (!el) return;
    const anchorEl = getAnchorRef.current();
    if (anchorEl) {
      const a = anchorEl.getBoundingClientRect();
      lastAnchor.current = { top: a.top, bottom: a.bottom, left: a.left };
    }
    const next = measureOnce(el, anchorEl, lastAnchor.current);
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, [ref]);

  // mount + çağıranın verdiği tetikleyiciler: boyamadan önce ölç (zıplama yok)
  useLayoutEffect(() => {
    measure();
  }, [measure, ...deps]);

  // kaydırma (herhangi bir kapta — capture) + pencere boyutu + popover'ın kendi boyutu
  useEffect(() => {
    let raf = 0;
    const schedule = (): void => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        measure();
      });
    };
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const el = ref.current;
    const ro = typeof ResizeObserver !== "undefined" && el ? new ResizeObserver(schedule) : null;
    if (el) ro?.observe(el);
    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [measure, ref]);

  return pos;
}

/** Popover kök stilinin konum parçası (fixed + katman + gerekirse iç kaydırma). */
export function placementStyle(pos: PlacedPosition): React.CSSProperties {
  return {
    position: "fixed",
    top: pos.top,
    left: pos.left,
    zIndex: POPOVER_Z_INDEX,
    ...(pos.maxHeight !== undefined ? { maxHeight: pos.maxHeight, overflowY: "auto" } : {}),
  };
}
